<template>
  <SectionCard :id="section.id" :title="section.title">
    <div class="grid gap-6">
      <div class="grid gap-4 xl:grid-cols-2">
        <section>
          <h3 :id="section.subSections.cashContribution.id" class="mb-3">
            {{ section.subSections.cashContribution.title }}
          </h3>
          <div class="calc-panel overflow-hidden rounded-md">
            <div
              v-for="row in cashContributionCalculationRows"
              :key="row.label"
              class="calc-row grid grid-cols-[2rem_minmax(0,1fr)] items-center gap-3 px-4 py-3 sm:grid-cols-[2rem_minmax(0,1fr)_auto]"
              :class="{ 'is-result': row.result }"
            >
              <span class="calc-operator text-center text-base font-semibold" :data-operator="row.operator">
                {{ row.operator }}
              </span>
              <div class="min-w-0">
                <p class="font-medium">{{ row.label }}</p>
                <p v-if="row.description" class="mt-0.5 text-xs text-subtle">{{ row.description }}</p>
              </div>
              <strong
                class="calc-value col-start-2 text-left whitespace-nowrap sm:col-start-auto sm:text-right"
                :class="{ 'text-base': row.result }"
                :data-variant="valueVariant(row)"
              >
                {{ row.value }}
              </strong>
            </div>
          </div>
        </section>

        <section v-if="customerMoneyCalculationRows.length">
          <h3 :id="customerMoneySection.id" class="mb-3">
            {{ customerMoneySection.title }}
          </h3>
          <div class="calc-panel overflow-hidden rounded-md">
            <div
              v-for="row in customerMoneyCalculationRows"
              :key="row.label"
              class="calc-row grid grid-cols-[2rem_minmax(0,1fr)] items-center gap-3 px-4 py-3 sm:grid-cols-[2rem_minmax(0,1fr)_auto]"
              :class="{ 'is-result': row.result }"
            >
              <span class="calc-operator text-center text-base font-semibold" :data-operator="row.operator">
                {{ row.operator }}
              </span>
              <div class="min-w-0">
                <p class="font-medium">{{ row.label }}</p>
                <p v-if="row.description" class="mt-0.5 text-xs text-subtle">{{ row.description }}</p>
              </div>
              <strong
                class="calc-value col-start-2 text-left whitespace-nowrap sm:col-start-auto sm:text-right"
                :class="{ 'text-base': row.result }"
                :data-variant="valueVariant(row)"
              >
                {{ row.value }}
              </strong>
            </div>
          </div>
        </section>
      </div>

      <section>
        <h3 :id="section.subSections.moneySource.id" class="mb-3">
          {{ section.subSections.moneySource.title }}
        </h3>
        <AppTable v-if="moneySources.length">
          <thead>
            <tr>
              <th>Ursprung</th>
              <th>Belopp</th>
              <th>Kommentar</th>
              <th>Underlag</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow v-for="(moneySource, index) in moneySources" :key="index">
              <AppTableCell>{{ formatMoneySourceLabel(moneySource.source) }}</AppTableCell>
              <AppTableCell nowrap>{{ formatAmountWithCurrency(moneySource.amount) }}</AppTableCell>
              <AppTableCell>{{ moneySource.sourceFreeText || '-' }}</AppTableCell>
              <AppTableCell>{{ moneySource.documentationStatus }}</AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <p v-else class="text-subtle">Inget ursprung angivet.</p>
      </section>

      <section>
        <h3 :id="documentsSection.id" class="mb-3">
          {{ documentsSection.title }}
        </h3>
        <AppTable>
          <thead>
            <tr>
              <th>Dokumenttyp</th>
              <th>Verifierat</th>
              <th></th>
            </tr>
          </thead>
          <tbody v-if="moneySourceDocuments.length">
            <AppTableRow v-for="document in moneySourceDocuments" :key="document.id">
              <AppTableCell>{{ translateDocumentType(document.type) }}</AppTableCell>
              <AppTableCell nowrap>{{ formatDateIfValid(document.createdAt) }}</AppTableCell>
              <AppTableCell action>
                <AppButton
                  icon
                  small
                  :aria-label="`Visa ${translateDocumentType(document.type)}`"
                  :loading="openingDocumentId === document.id"
                  title="Visa"
                  @click="openDocument(document)"
                >
                  <SvgIcon :path="mdiMagnify" :size="18" />
                </AppButton>
              </AppTableCell>
            </AppTableRow>
          </tbody>

          <tbody v-else>
            <AppTableRow>
              <AppTableCell colspan="3" class="text-center"> Inga dokument tillagda </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
      </section>

      <section v-if="hasBridgeOrDepositLoans || propertySales.length">
        <h3 :id="section.subSections.bridgeAndDepositLoans.id" class="mb-3">
          {{ section.subSections.bridgeAndDepositLoans.title }}
        </h3>

        <div v-if="propertySales.length" class="grid gap-3">
          <article v-for="(propertySale, index) in propertySales" :key="index" class="sale-card rounded-md p-3">
            <h4 class="mb-3">Bostadsförsäljning {{ propertySales.length > 1 ? index + 1 : '' }}</h4>
            <dl class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <div v-for="row in getPropertySaleRows(propertySale)" :key="row.label" class="info-tile rounded-md p-3">
                <dt class="text-xs text-subtle">{{ row.label }}</dt>
                <dd class="mt-1 font-medium">{{ row.value }}</dd>
              </div>
            </dl>
          </article>

          <div>
            <h4 class="mb-2">Beräkning</h4>
            <AppTable>
              <thead>
                <tr>
                  <th>Försäljningspris</th>
                  <th>Mäklararvode</th>
                  <th>Kvarvarande lån</th>
                  <th>Vinstskatt</th>
                  <th>Överskott</th>
                </tr>
              </thead>
              <tbody>
                <AppTableRow v-for="(propertySale, index) in propertySales" :key="index">
                  <AppTableCell nowrap>
                    <strong class="positive">{{ formatAmountWithCurrency(propertySale.salePrice ?? 0) }}</strong>
                  </AppTableCell>
                  <AppTableCell nowrap>
                    <strong class="negative">{{ formatAmountWithCurrency(propertySale.saleFee ?? 0) }}</strong>
                  </AppTableCell>
                  <AppTableCell nowrap>
                    <strong class="negative">{{ formatAmountWithCurrency(propertySale.saleLoans ?? 0) }}</strong>
                  </AppTableCell>
                  <AppTableCell nowrap>
                    <strong class="negative">{{
                      formatAmountWithCurrency(calculatePropertySaleTax(propertySale))
                    }}</strong>
                  </AppTableCell>
                  <AppTableCell nowrap>
                    <strong class="result">{{ formatAmountWithCurrency(propertySale.excessFromSales) }}</strong>
                  </AppTableCell>
                </AppTableRow>

                <AppTableRow v-if="propertySales.length > 1">
                  <AppTableCell colspan="4" />
                  <AppTableCell nowrap>
                    <strong class="result">{{ formatAmountWithCurrency(totalExcessFromSales) }}</strong>
                  </AppTableCell>
                </AppTableRow>
              </tbody>
            </AppTable>
          </div>
        </div>

        <div>
          <h4 class="mb-4">Brygg- och handpenningslån</h4>

          <dl v-if="hasBridgeOrDepositLoans" class="mb-4 grid gap-2 sm:grid-cols-2">
            <div v-if="hasDepositLoan" class="info-tile rounded-md p-3">
              <dt class="text-xs text-subtle">Handpenningslån</dt>
              <dd class="mt-1 font-medium">{{ formatGrantedAmount(depositLoan?.amount) }}</dd>
              <template v-if="depositLoan?.amountAppliedFor">
                <dt class="mt-2 text-xs text-subtle">Ansökt belopp</dt>
                <dd class="mt-1">{{ formatAmountWithCurrency(depositLoan.amountAppliedFor) }}</dd>
              </template>
              <template v-if="depositLoan?.discounts.length">
                <dt class="mt-2 text-xs text-subtle">Rabatt</dt>
                <dd v-for="(discount, index) in depositLoan.discounts" :key="`deposit-discount-${index}`" class="mt-1">
                  {{ discount.bps }} punkter
                  <div v-if="discount.reason" class="text-subtle text-xs">{{ discount.reason }}</div>
                </dd>
              </template>
              <template v-if="depositLoan?.paidDate">
                <dt class="mt-2 text-xs text-subtle">Utbetald</dt>
                <dd class="mt-1">{{ formatDateIfValid(depositLoan.paidDate) }}</dd>
              </template>
              <template v-else-if="depositLoan?.plannedPayoutDate">
                <dt class="mt-2 text-xs text-subtle">Planerad utbetalning</dt>
                <dd class="mt-1">{{ formatDateIfValid(depositLoan.plannedPayoutDate) }}</dd>
              </template>
              <dd v-if="depositLoan?.id" class="deposit-loan-link-row">
                <AppRouterLink :to="`/handpenningslan/${depositLoan.id}`" variant="button" class="deposit-loan-link">
                  Till handpenningslånet
                </AppRouterLink>
              </dd>
            </div>
            <div v-if="hasBridgeLoan" class="info-tile rounded-md p-3">
              <dt class="text-xs text-subtle">Brygglån</dt>
              <dd class="mt-1 font-medium">{{ formatGrantedAmount(unsecuredLoansBreakdown.bridgeLoanAmount) }}</dd>
              <template v-if="unsecuredLoansBreakdown.bridgeLoanAmountAppliedFor">
                <dt class="mt-2 text-xs text-subtle">Ansökt belopp</dt>
                <dd class="mt-1">{{ formatAmountWithCurrency(unsecuredLoansBreakdown.bridgeLoanAmountAppliedFor) }}</dd>
              </template>
              <template v-if="unsecuredLoansBreakdown.bridgeLoanDiscounts.length">
                <dt class="mt-2 text-xs text-subtle">Rabatt</dt>
                <dd
                  v-for="(discount, index) in unsecuredLoansBreakdown.bridgeLoanDiscounts"
                  :key="`bridge-discount-${index}`"
                  class="mt-1"
                >
                  {{ discount.bps }} punkter
                  <div v-if="discount.reason" class="text-subtle text-xs">{{ discount.reason }}</div>
                </dd>
              </template>
            </div>
          </dl>
        </div>
      </section>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiMagnify } from '@mdi/js'
import { formatAmountWithCurrency, formatBooleanValue, formatDateIfValid, toNumericValue } from '@staff/shared'
import { AppButton, AppTable, AppTableCell, AppTableRow, SectionCard, SvgIcon } from '@staff/ui'

import type {
  ApplicationDetailsMortgageDeedResponse,
  ApplicationDetailsPropertySaleResponse,
  DownPaymentMoneySource,
} from '@/services/application'
import AppRouterLink from '@/components/AppRouterLink.vue'
import { useMetadata } from '@/composables/useMetadata'
import type { ApplicationSections } from './sections'
import { useDocumentPreview } from '@/composables/documentPreview'
import { useApplicationDocumentsContext, getDocumentsByType } from '@/views/application/applicationDocuments.context'
import { useRequiredApplication } from '@/views/application/application.context'
import { isRealEstateCollateral } from '@/utils/applicationDisplay'

const customerMoneySection = { id: 'kontantinsats-customer-money', title: 'Pengar som kunden behöver ha' } as const
const documentsSection = { id: 'kontantinsats-documents', title: 'Dokument' } as const

const { cashContributionOptions, translateDocumentType } = useMetadata()

defineProps<{
  section: ApplicationSections['cashContribution']
}>()

const application = useRequiredApplication()
const { documents } = useApplicationDocumentsContext()
const { openingDocumentId, openDocument } = useDocumentPreview((document) => translateDocumentType(document.type))

type DisplayRow = {
  label: string
  value: string
}

type CalculationRow = DisplayRow & {
  operator: '+' | '-' | '='
  description?: string
  result?: boolean
  tone?: 'positive' | 'negative' | 'neutral'
}

type MoneySourceDisplay = {
  source: string
  amount: number
  sourceFreeText?: string
  documentationStatus: string
}

const collateral = computed(() => application.value.collateralProperty)

const propertyValue = computed(() => {
  return toNumericValue(collateral.value?.valuations.currentValuation?.value)
})

const amountApproved = computed(() => application.value.amountApproved ?? undefined)
const storedDownPayment = computed(() => application.value.downPaymentSource?.downPayment ?? undefined)
const hasStoredDownPayment = computed(() => storedDownPayment.value !== undefined)
const downPayment = computed(() => {
  if (storedDownPayment.value !== undefined) return storedDownPayment.value
  return propertyValue.value !== undefined ? propertyValue.value / 10 : undefined
})

const propertySales = computed(() => application.value.unsecuredLoansBreakdown.propertySales ?? [])

const unsecuredLoansBreakdown = computed(() => application.value.unsecuredLoansBreakdown)
const depositLoan = computed(() => unsecuredLoansBreakdown.value.depositLoan)
const depositLoanAmount = computed(() => depositLoan.value?.amount ?? 0)
const bridgeLoanAmount = computed(() => unsecuredLoansBreakdown.value.bridgeLoanAmount ?? 0)

const hasDepositLoan = computed(() => depositLoanAmount.value > 0 || !!depositLoan.value?.amountAppliedFor)
const hasBridgeLoan = computed(
  () => bridgeLoanAmount.value > 0 || !!unsecuredLoansBreakdown.value.bridgeLoanAmountAppliedFor,
)
const hasBridgeOrDepositLoans = computed(() => hasDepositLoan.value || hasBridgeLoan.value)

const formatGrantedAmount = (amount: number | null | undefined) =>
  amount ? formatAmountWithCurrency(amount) : 'Ej beviljat ännu'

const amountAtHandover = computed(() => {
  if (propertyValue.value === undefined || downPayment.value === undefined) {
    return undefined
  }

  return propertyValue.value - downPayment.value - depositLoanAmount.value
})

const remainingCashContribution = computed(() => {
  if (amountAtHandover.value === undefined || amountApproved.value === undefined) {
    return undefined
  }

  return amountAtHandover.value - amountApproved.value - bridgeLoanAmount.value
})

const mortgageDeeds = computed<ApplicationDetailsMortgageDeedResponse[]>(() =>
  isRealEstateCollateral(collateral.value) ? (collateral.value?.mortgageDeeds ?? []) : [],
)
const newMortgageDeedsAmount = computed(() =>
  mortgageDeeds.value
    .filter((mortgageDeed) => mortgageDeed.use === true && mortgageDeed.exists === false)
    .reduce((sum, mortgageDeed) => sum + mortgageDeed.amount, 0),
)

const newMortgageDeedsCost = computed(() => {
  if (!newMortgageDeedsAmount.value) {
    return undefined
  }

  return Math.max(Math.floor(newMortgageDeedsAmount.value / 1000) * 1000 * 0.02, 50) + 375
})

const newTitleDeedCost = computed(() => {
  if (!isRealEstateCollateral(collateral.value) || propertyValue.value === undefined) {
    return undefined
  }

  return Math.max(Math.floor(propertyValue.value / 1000) * 1000 * 0.015, 50) + 825
})

const cashContributionCalculationRows = computed<CalculationRow[]>(() => {
  const rows: CalculationRow[] = [
    {
      label: 'Köpeskilling ny bostad',
      value: formatAmountWithCurrency(propertyValue.value),
      operator: '+',
      tone: 'positive',
    },
    {
      label: 'Erlagd handpenning',
      value: formatAmountWithCurrency(downPayment.value),
      operator: '-',
      tone: 'negative',
      description: hasStoredDownPayment.value ? undefined : 'Standardvärde 10 % av köpeskillingen',
    },
  ]

  if (depositLoanAmount.value > 0) {
    rows.push({
      label: 'Totalt handpenningslån',
      value: formatAmountWithCurrency(depositLoanAmount.value),
      operator: '-',
      tone: 'negative',
    })
  }

  rows.push(
    {
      label: 'Att erlägga vid tillträdet',
      value: formatAmountWithCurrency(amountAtHandover.value),
      operator: '=',
      result: true,
    },
    {
      label: 'Bolånets storlek',
      value: formatAmountWithCurrency(amountApproved.value),
      operator: '-',
      tone: 'negative',
    },
  )

  if (bridgeLoanAmount.value > 0) {
    rows.push({
      label: 'Totalt brygglån',
      value: formatAmountWithCurrency(bridgeLoanAmount.value),
      operator: '-',
      tone: 'negative',
    })
  }

  rows.push({
    label: 'Kvar att erlägga för kund',
    value: formatAmountWithCurrency(
      remainingCashContribution.value === undefined ? undefined : Math.abs(remainingCashContribution.value),
    ),
    operator: '=',
    result: true,
    tone: remainingCashContribution.value !== undefined && remainingCashContribution.value < 0 ? 'negative' : undefined,
  })

  return rows
})

const customerMoneyCalculationRows = computed<CalculationRow[]>(() => {
  const rows: CalculationRow[] = []

  if (newMortgageDeedsAmount.value > 0) {
    rows.push({
      label: 'Kostnad för nya pantbrev',
      value: formatAmountWithCurrency(newMortgageDeedsCost.value),
      operator: '+',
      description: `2 % stämpelskatt + 375 kr expeditionsavgift på ${formatAmountWithCurrency(newMortgageDeedsAmount.value)}`,
      tone: 'negative',
    })
  }

  if (newTitleDeedCost.value !== undefined) {
    rows.push({
      label: 'Kostnad för lagfart',
      value: formatAmountWithCurrency(newTitleDeedCost.value),
      operator: '+',
      description: '1,5 % stämpelskatt + 825 kr expeditionsavgift',
      tone: 'negative',
    })
  }

  if (rows.length) {
    rows.push({
      label: 'Totalt',
      value: formatAmountWithCurrency((newMortgageDeedsCost.value ?? 0) + (newTitleDeedCost.value ?? 0)),
      operator: '=',
      result: true,
    })
  }

  return rows
})

const valueVariant = (row: CalculationRow) => {
  if (row.result) {
    return row.tone === 'negative' ? 'negative' : 'result'
  }

  return row.tone ?? 'neutral'
}

const formatDocumentationStatus = (moneySource: DownPaymentMoneySource) => {
  if (moneySource.receivedDocumentation) return 'Mottaget'
  if (moneySource.needsDocumentation) return 'Saknas'
  return 'Krävs ej'
}

const moneySources = computed<MoneySourceDisplay[]>(() =>
  (application.value.downPaymentSource?.cashContribution ?? []).map((moneySource) => ({
    source: moneySource.source,
    amount: moneySource.amount,
    ...(moneySource.source === 'other' ? { sourceFreeText: moneySource.sourceFreeText } : {}),
    documentationStatus: formatDocumentationStatus(moneySource),
  })),
)
const moneySourceDocuments = computed(() => getDocumentsByType(documents.value, 'moneySource'))

const formatMoneySourceLabel = (source: string) => {
  return cashContributionOptions.value.find((opt) => opt.code === source)?.label ?? source
}

const getPropertySaleRows = (propertySale: ApplicationDetailsPropertySaleResponse): DisplayRow[] => [
  { label: 'Försäljningspris', value: formatAmountWithCurrency(propertySale.salePrice) },
  { label: 'Köpeskilling på såld bostad', value: formatAmountWithCurrency(propertySale.purchasePrice) },
  { label: 'Nuvarande lån på bostaden', value: formatAmountWithCurrency(propertySale.saleLoans) },
  { label: 'Mäklararvode', value: formatAmountWithCurrency(propertySale.saleFee) },
  { label: 'Tidigare uppskov', value: formatAmountWithCurrency(propertySale.deferredTax) },
  { label: 'Skatta direkt', value: formatBooleanValue(propertySale.taxNow) },
  { label: 'Datum frånträde', value: formatDateIfValid(propertySale.saleCompleteDate) ?? '-' },
]

/**
 * Vinstskatten på en bostadsförsäljning, räknad som periscope gör det idag
 * (`ApplicationService.calculatePropertySale`). `deferredTax` är *tidigare uppskov* — inte
 * skatten — så den går in i beräkningen och visas inte som resultat.
 *
 * Väljer kunden att skatta direkt beskattas vinsten plus det tidigare uppskovet. Skjuts skatten
 * upp beskattas bara det som inte ryms under uppskovstaket (3 000 000 kr minus redan använt
 * uppskov). Skattesatsen är 22 %.
 *
 * Beräkningen ska bort härifrån i en egen ticket — tills dess speglar den periscope, så samma
 * ansökan visar samma siffra i båda apparna.
 */
const DEFERRED_TAX_CAP = 3_000_000
const TAX_RATE = 0.22

const calculatePropertySaleTax = (propertySale: ApplicationDetailsPropertySaleResponse) => {
  const salePrice = propertySale.salePrice ?? 0
  const purchasePrice = propertySale.purchasePrice ?? 0
  const saleFee = propertySale.saleFee ?? 0
  const deferredTax = propertySale.deferredTax ?? 0
  const amountToTax = Math.max(salePrice - purchasePrice - saleFee, 0)

  return propertySale.taxNow
    ? Math.ceil((deferredTax + amountToTax) * TAX_RATE)
    : Math.ceil(Math.max(amountToTax - (DEFERRED_TAX_CAP - deferredTax), 0) * TAX_RATE)
}

const totalExcessFromSales = computed(() =>
  propertySales.value.reduce((sum, propertySale) => sum + propertySale.excessFromSales, 0),
)
</script>

<style scoped lang="scss">
// Calc row colours, grouped by colour and keyed off operator / value variant.
// calc-value default
.calc-value,
.calc-operator[data-operator='='] {
  color: var(--color-fg-default);
}

// Success — plus operator & positive values
.calc-operator[data-operator='+'],
.calc-value[data-variant='positive'] {
  color: var(--color-success-fg);
}

// Danger — minus operator & any negative value
.calc-operator[data-operator='-'],
.calc-value[data-variant='negative'] {
  color: var(--color-danger-fg);
}

// Brand emphasis — result total value
.calc-value[data-variant='result'] {
  color: var(--color-brand-emphasis);
}

.calc-row {
  border-bottom: 1px solid var(--color-border-divider);
  background-color: var(--color-canvas-default);

  &:last-child {
    border-bottom: 0;
  }

  &.is-result {
    background-color: var(--color-brand-subtle);
  }
}

.info-tile {
  background-color: var(--color-canvas-inset);
}

.deposit-loan-link-row {
  margin-top: var(--space-8);
}

.deposit-loan-link {
  display: inline-block;
  background-color: var(--color-button-primary-bg);
  color: var(--color-fg-on-emphasis);

  &:hover {
    background-color: var(--color-button-primary-hover-bg);
  }
}

.calc-panel,
.sale-card {
  border: 1px solid var(--color-border-muted);
  background-color: var(--color-canvas-default);
}

// Sale calculation signs, mirroring the payout calculation in periscope.
.positive,
.negative,
.result {
  font-weight: 500;
}

.positive {
  color: var(--color-success-fg);

  &::before {
    content: '+ ';
  }
}

.negative {
  color: var(--color-danger-fg);

  &::before {
    content: '- ';
  }
}

.result {
  color: var(--color-accent-fg);

  &::before {
    content: '= ';
  }
}
</style>
